import { html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import { nsFormFieldStyles } from '../../../ns-styles/index.js';
import { renderFormField } from '../../../helpers/form-field.js';
import NSJobComponent from '../job.js';
import JobSchema from '../../../models/job-schema.js';
import definition from '@job-schemas/form.json';
import { jobSchema } from '../job-schema.js';
import {
  answersToSend,
  configOf,
  formErrors,
  formItems,
  isSection,
  visibleItems,
  type Answers,
  type FormField,
  type FormItem,
} from '../generic-logic.js';
import '../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/checkbox/checkbox.js';
import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';

// A form whose questions come from its node's config: sections become panels, and a question shows only while its show-if holds
@customElement(NSForm.is)
export default class NSForm extends NSJobComponent {
  static get is() {
    return 'ns-form';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { submit: { type: 'hidden' }, decline: { type: 'hidden' } } });
  }

  @state() private __answers: Answers = {};

  @state() private __errors: Record<string, string> = {};

  @state() private __declining = false;

  @state() private __reason = '';

  static styles = [
    nsFormFieldStyles,
    ...super.styles,
    css`
      textarea {
        width: 100%;
        min-height: 5em;
      }

      /* A group's question reads as every other field label does */
      .group-label,
      wa-radio-group::part(form-control-label) {
        font-size: var(--wa-font-size-m);
        font-weight: 600;
        color: var(--primary-card-text-color);
      }

      .options {
        display: flex;
        flex-direction: column;
        gap: var(--wa-space-2xs);
      }
    `,
  ];

  private get __items(): FormItem[] {
    return formItems(configOf(this.job));
  }

  private __set(key: string, value: unknown) {
    this.__answers = { ...this.__answers, [key]: value };
    if (this.__errors[key]) this.__errors = { ...this.__errors, [key]: '' };
  }

  private async __send(outcome: 'submit' | 'decline') {
    if (outcome === 'submit') {
      this.__errors = formErrors(this.__items, this.__answers);
      if (Object.keys(this.__errors).length > 0) return;
    }
    const answers =
      outcome === 'submit'
        ? answersToSend(this.__items, this.__answers)
        : this.__reason.trim()
          ? { reason: this.__reason.trim() }
          : {};
    try {
      this.loading(outcome === 'submit' ? 'Sending your answers' : 'Declining');
      await this.outcome(outcome, { ANSWERS: answers });
      this.loaded();
    } catch (err) {
      this.loaded();
      this.failJob(err, outcome === 'submit' ? 'Unable to send your answers' : 'Unable to decline');
    }
  }

  private __control(field: FormField, id: string, label: string): TemplateResult {
    const value = this.__answers[field.key];
    const text = (e: Event) => (e.target as HTMLInputElement | HTMLTextAreaElement).value;
    switch (field.kind) {
      case 'longText':
        return html`
          <textarea
            id=${id}
            maxlength="10000"
            .value=${live(typeof value === 'string' ? value : '')}
            @input=${(e: Event) => this.__set(field.key, text(e))}
          ></textarea>
        `;
      case 'number':
        return html`
          <input
            id=${id}
            type="number"
            .value=${live(typeof value === 'number' ? String(value) : '')}
            @input=${(e: Event) => this.__set(field.key, text(e) === '' ? undefined : Number(text(e)))}
          />
        `;
      case 'date':
        return html`
          <input
            id=${id}
            type="date"
            .value=${live(typeof value === 'string' ? value : '')}
            @input=${(e: Event) => this.__set(field.key, text(e) || undefined)}
          />
        `;
      case 'yesNo':
        return html`
          <wa-radio-group
            id=${id}
            label=${label}
            orientation="horizontal"
            .value=${value === true ? 'YES' : value === false ? 'NO' : ''}
            @change=${(e: Event) => this.__set(field.key, (e.target as HTMLInputElement).value === 'YES')}
          >
            <wa-radio value="YES">Yes</wa-radio>
            <wa-radio value="NO">No</wa-radio>
          </wa-radio-group>
        `;
      case 'choice':
        return html`
          <wa-radio-group
            id=${id}
            label=${label}
            .value=${typeof value === 'string' ? value : ''}
            @change=${(e: Event) => this.__set(field.key, (e.target as HTMLInputElement).value)}
          >
            ${(field.options ?? []).map(
              (option) => html`
                <wa-radio value=${option.key}>${option.label}</wa-radio>
              `,
            )}
          </wa-radio-group>
        `;
      case 'multiChoice': {
        const picked = Array.isArray(value) ? (value as string[]) : [];
        return html`
          <div class="options" id=${id} role="group" aria-label=${label}>
            ${(field.options ?? []).map(
              (option) => html`
                <wa-checkbox
                  .checked=${picked.includes(option.key)}
                  @change=${(e: Event) => {
                    const on = (e.target as HTMLInputElement).checked;
                    const next = on ? [...picked, option.key] : picked.filter((key) => key !== option.key);
                    // Kept in the form's order of options
                    this.__set(
                      field.key,
                      (field.options ?? []).map((o) => o.key).filter((key) => next.includes(key)),
                    );
                  }}
                >
                  ${option.label}
                </wa-checkbox>
              `,
            )}
          </div>
        `;
      }
      default:
        return html`
          <input
            id=${id}
            maxlength="2000"
            .value=${live(typeof value === 'string' ? value : '')}
            @input=${(e: Event) => this.__set(field.key, text(e))}
          />
        `;
    }
  }

  private __field(field: FormField) {
    const id = `field-${field.key}`;
    const label = field.required ? field.label : `${field.label} (optional)`;
    const error = this.__errors[field.key] || undefined;
    // A group of choices names itself, as a label element can't point at one
    if (field.kind === 'yesNo' || field.kind === 'choice' || field.kind === 'multiChoice') {
      return html`
        <div class="field">
          ${field.kind === 'multiChoice'
            ? html`
                <span class="group-label" aria-hidden="true">${label}</span>
              `
            : nothing}
          ${this.__control(field, id, label)}
          ${field.help
            ? html`
                <p class="hint">${field.help}</p>
              `
            : nothing}
          ${error
            ? html`
                <div class="field-error" role="alert">${error}</div>
              `
            : nothing}
        </div>
      `;
    }
    return renderFormField({ id, label, control: this.__control(field, id, label), hint: field.help, error });
  }

  // Each section opens a panel; questions before the first section share one without a heading
  private __panels() {
    const panels: { heading?: string; fields: FormField[] }[] = [];
    let current: { heading?: string; fields: FormField[] } | undefined;
    for (const item of visibleItems(this.__items, this.__answers)) {
      if (isSection(item) || !current) {
        current = { heading: isSection(item) ? item.label : undefined, fields: [] };
        panels.push(current);
      }
      if (!isSection(item)) current.fields.push(item);
    }
    return panels.filter((panel) => panel.fields.length > 0);
  }

  renderActions() {
    if (this.__declining) {
      return html`
        <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__declining = false)}>
          Back
        </wa-button>
        <wa-button variant="brand" type="button" @click=${() => this.__send('decline')}>Decline</wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => (this.__declining = true)}>
        Decline
      </wa-button>
      <wa-button variant="brand" type="button" @click=${() => this.__send('submit')}>Submit</wa-button>
    `;
  }

  render() {
    const intro = configOf(this.job).intro;
    return html`
      <ns-job-layout>
        ${typeof intro === 'string' && intro
          ? html`
              <p slot="intro">${intro}</p>
            `
          : nothing}
        ${this.__declining
          ? html`
              <div class="job-panel">
                <h3>Decline this form</h3>
                ${renderFormField({
                  id: 'decline-reason',
                  label: 'Why (optional)',
                  control: html`
                    <textarea
                      id="decline-reason"
                      maxlength="10000"
                      .value=${live(this.__reason)}
                      @input=${(e: Event) => (this.__reason = (e.target as HTMLTextAreaElement).value)}
                    ></textarea>
                  `,
                })}
              </div>
            `
          : this.__panels().map(
              (panel) => html`
                <div class="job-panel">
                  ${panel.heading
                    ? html`
                        <h3>${panel.heading}</h3>
                      `
                    : nothing}
                  ${panel.fields.map((field) => this.__field(field))}
                </div>
              `,
            )}
      </ns-job-layout>
    `;
  }
}
